import axios from "axios";

const API_URL = "https://api.thunderbirdsmoke.net";

// Helper to add Authorization header
const authHeader = () => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("No auth token found");
  }
  return { Authorization: `Bearer ${token}` };
};

/**
 * Order Management APIs
 */

// Fetch all orders
export interface Order {
  order_id: number;
  order_number: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  shipping_address?: {
    label?: string;
    full_name?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  } | null;
  direct_shipping_address?: string;
  payment_status: string;
  payment_method: string;
  subtotal: number;
  discount_type: "percent" | "fixed" | "none";
  discount_value: number;
  loyalty_points_used: number;
  tax: number;
  shipping_cost: number;
  total_price: number;

  /** 🆕 Delivery fields */
  is_delivery?: boolean;
  delivery_fee?: number;
  delivery_status?: string;

  created_at: string;
  is_online_order: boolean;
  store_id?: number;
  store_name?: string;

  items: {
    image_url?: string;
    attributes: any[];
    product_id: number;
    quantity: number;
    price: number;
    product_name: string;
    variant_name: string;
  }[];
}


export const fetchOrders = async (params: { source?: string; limit?: number } = {}): Promise<Order[]> => {
  const response = await axios.get<Order[]>(`${API_URL}/orders`, {
    headers: authHeader(),
    params, // This sends ?source=online&limit=5
  });

  return response.data.map((order) => ({
    ...order,
    subtotal: Number(order.subtotal) || 0,
    discount_value: Number(order.discount_value) || 0,
    loyalty_points_used: Number(order.loyalty_points_used) || 0,
    tax: Number(order.tax) || 0,
    shipping_cost: Number(order.shipping_cost) || 0,
    total_price: Number(order.total_price) || 0,
    payment_status: order.payment_status || "pending",
    payment_method: order.payment_method || "N/A",
    shipping_address: order.shipping_address || {
      address: order.direct_shipping_address,
      city: "",
      state: "",
      zip_code: ""
    }
  }));
};


// Create a new order
export const createOrder = async (orderData: {
  customer_name: string;
  items: { product_id: number; quantity: number }[];
}) => {
  const response = await axios.post(`${API_URL}/orders`, orderData, {
    headers: authHeader(),
  });
  return response.data;
};

// Update an order's payment status
export const updateOrder = async (orderId: number, updateData: { payment_status: string }) => {
  const response = await axios.put(`${API_URL}/orders/${orderId}`, updateData, {
    headers: authHeader(),
  });
  return response.data;
};

// Delete an order
export const deleteOrder = async (orderId: number) => {
  const response = await axios.delete(`${API_URL}/orders/${orderId}`, {
    headers: authHeader(),
  });
  return response.data;
};

// Generate receipt
export const generateReceipt = async (orderId: number) => {
  const response = await axios.get(`${API_URL}/orders/${orderId}/receipt`, {
    headers: authHeader(),
  });
  return response.data;
};

export const sendReceipt = async (orderId: number) => {
  const response = await axios.post(`${API_URL}/pos/orders/${orderId}/send-receipt`, null, {
    headers: authHeader(),
  });
  return response.data;
};